import React from 'react';

import cs from 'classnames';

import {
  ControlStyles,
  SelectMenuListStyles,
  SelectMenuStyles,
  SelectOptionStyles,
  selectStyles,
  SweetReactSelect,
} from 'app/components/general/SweetReactSelect';
import {gt} from 'app/utils/i18n';

import type {SingleValueProps} from 'react-select/lib/components/SingleValue';

const IconPickerSingleValue = (props: SingleValueProps) => {
  const {data} = props;
  return (
    <div className="d-flex">
      <i className={cs(data.value, 'fa-fw')} />
    </div>
  );
};

type Props = {
  icon: string;
  onChange: (choice: string) => unknown;
  disabled?: boolean;
};
export function KBTreeIconPicker({icon, onChange, disabled}: Props) {
  return (
    <SweetReactSelect
      placeholder=""
      components={{
        SingleValue: IconPickerSingleValue,
      }}
      styles={{
        ...selectStyles,
        menu: (styles) => ({...SelectMenuStyles(styles), width: 230}),
        menuList: (styles) => ({
          ...SelectMenuListStyles(styles),
          padding: 10,
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }),
        option: (styles, props) => ({
          ...SelectOptionStyles(styles, props),
          width: 'auto',
        }),
        control: (styles, props) => ({
          ...ControlStyles(styles, props),
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
          borderRight: 'none',
        }),
      }}
      isDisabled={disabled}
      isMulti={false}
      blurInputOnSelect={true}
      isClearable={false}
      isSearchable={false}
      value={{value: icon}}
      formatOptionLabel={(icon) => (
        <i title={icon.label} className={cs(icon.value, 'fa-fw')} />
      )}
      onChange={(i) => (i != null ? onChange(i.value) : null)}
      options={[
        {value: 'far fa-folder', label: gt('Folder')},
        {value: 'fas fa-code', label: gt('Code')},
        {value: 'fa fa-coffee', label: gt('Coffee')},
        {value: 'fal fa-rocket', label: gt('Rocket')},
        {value: 'fa fa-umbrella', label: gt('Umbrella')},
        {value: 'far fa-file', label: gt('File')},
        {value: 'far fa-copy', label: gt('Copy')},
        {value: 'far fa-shield-alt', label: gt('Shield')},
        {value: 'fas fa-cog', label: gt('Cog')},
        {value: 'fa fa-tasks', label: gt('Tasks')},
        {value: 'fa fa-users', label: gt('Users')},
        {value: 'fas fa-exclamation-triangle', label: gt('Warning')},
        {value: 'far fa-book-open', label: gt('Book Open')},
        {value: 'fas fa-bicycle', label: gt('Bicycle')},
        {value: 'fas fa-camera', label: gt('Camera')},
        {value: 'far fa-bell', label: gt('Bell')},
        {value: 'fas fa-anchor', label: gt('Anchor')},
        {value: 'fas fa-apple-alt', label: gt('Apple')},
        {value: 'fas fa-battery-half', label: gt('Battery')},
        {value: 'fas fa-birthday-cake', label: gt('Birthday Cake')},
        {value: 'fas fa-bookmark', label: gt('Bookmark')},
        {value: 'far fa-calendar-alt', label: gt('Calendar')},
        {value: 'fas fa-camera-retro', label: gt('Retro Camera')},
        {value: 'fas fa-car', label: gt('Car')},
        {value: 'fas fa-chart-bar', label: gt('Chart Bar')},
        {value: 'far fa-check-circle', label: gt('Check Circle')},
        {value: 'far fa-cloud', label: gt('Cloud')},
        {value: 'far fa-comment-dots', label: gt('Comment Dots')},
        {value: 'fas fa-compass', label: gt('Compass')},
        {value: 'far fa-envelope', label: gt('Envelope')},
        {value: 'fas fa-eye', label: gt('Eye')},
        {value: 'fas fa-feather', label: gt('Feather')},
        {value: 'fas fa-gem', label: gt('Gem')},
        {value: 'far fa-heart', label: gt('Heart')},
        {value: 'fas fa-key', label: gt('Key')},
        {value: 'far fa-laugh', label: gt('Laugh')},
        {value: 'fas fa-lemon', label: gt('Lemon')},
        {value: 'fas fa-music', label: gt('Music')},
        {value: 'fas fa-paw', label: gt('Paw')},
        {value: 'fas fa-plane', label: gt('Plane')},
        {value: 'fas fa-play-circle', label: gt('Play Circle')},
        {value: 'far fa-question-circle', label: gt('Question Circle')},
        {value: 'fas fa-rocket', label: gt('Rocket')},
        {value: 'far fa-sun', label: gt('Sun')},
        {value: 'fas fa-sync-alt', label: gt('Sync')},
        {value: 'fas fa-taxi', label: gt('Taxi')},
        {value: 'far fa-thumbs-up', label: gt('Thumbs Up')},
        {value: 'fas fa-wifi', label: gt('WiFi')},
        {value: 'fas fa-wrench', label: gt('Wrench')},
        {value: 'fas fa-shopping-cart', label: gt('Shopping Cart')},
        {value: 'far fa-credit-card', label: gt('Credit Card')},
        {value: 'fas fa-balance-scale', label: gt('Balance Scale')},
        {value: 'fas fa-gavel', label: gt('Gavel')},
        {value: 'far fa-building', label: gt('Building')},
        {value: 'fas fa-landmark', label: gt('Landmark')},
        {value: 'fas fa-briefcase', label: gt('Briefcase')},
        {value: 'fas fa-chart-pie', label: gt('Pie Chart')},
        {value: 'fas fa-user-tie', label: gt('User Tie')},
        {value: 'far fa-handshake', label: gt('Handshake')},
        {value: 'fas fa-truck', label: gt('Truck')},
        {value: 'fas fa-cash-register', label: gt('Cash Register')},
        {value: 'fas fa-baby-carriage', label: gt('Baby Carriage')},
        {value: 'far fa-calendar-check', label: gt('Calendar Check')},
        {value: 'fas fa-warehouse', label: gt('Warehouse')},
        {value: 'fas fa-chart-line', label: gt('Chart Line')},
        {value: 'fas fa-laptop', label: gt('Laptop')},
        {value: 'fas fa-graduation-cap', label: gt('Graduation Cap')},
        {value: 'fas fa-globe', label: gt('Globe')},
        {value: 'fas fa-file-contract', label: gt('File Contract')},
        {value: 'fas fa-suitcase', label: 'Suitcase'},
        {value: 'fas fa-user-secret', label: gt('User Secret')},
        {value: 'fas fa-user-shield', label: gt('User Shield')},
      ]}
    />
  );
}
